// REST client for telik-auth-service, replacing the old grpc-web
// AuthenticationService (@rapidaai/react) that used to live on telik-web-api.
//
// Notable shape choices, mirrored from the Go server (telik-auth-service/internal/domain):
// - `legacy_user_id`, and every organization/project/feature-permission id, are
//   JSON *strings* even though they're numeric on the server — 64-bit
//   snowflake IDs exceed what JS's Number type can represent exactly, so the
//   server encodes them as strings and callers must not coerce them to number.
import { CONFIG } from '@/configs';

const AUTH_API = CONFIG.connection.auth;

export interface AuthUserInfo {
  id: string;
  name?: string;
  email: string;
  created_at: string;
  provider: string;
  avatar_url?: string;
  legacy_user_id?: string;
}

export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  expires_at: string;
  user: AuthUserInfo;
}

export interface RegistrationResponse {
  email_confirmation_required: boolean;
  account_creation_status?: string;
  confirmation_delivery_status?: string;
  access_token?: string;
  refresh_token?: string;
  expires_at?: string;
  user?: AuthUserInfo;
}

export interface OrganizationRoleInfo {
  id: string;
  organization_id: string;
  role: string;
  organization_name: string;
}

export interface ProjectRoleInfo {
  id: string;
  project_id: string;
  role: string;
  project_name: string;
}

export interface FeaturePermissionInfo {
  id: string;
  feature: string;
  is_enable: boolean;
}

export interface SessionResponse {
  user: AuthUserInfo;
  organization_role?: OrganizationRoleInfo;
  project_roles: ProjectRoleInfo[];
  feature_permissions: FeaturePermissionInfo[];
}

interface ErrorResponse {
  error: string;
  message: string;
  correlation_id: string;
}

export class AuthApiError extends Error {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${AUTH_API}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  });
  const body = await res.json();
  if (!res.ok) {
    const err = body as ErrorResponse;
    throw new AuthApiError(
      err.error || 'unknown_error',
      err.message || 'Unable to process your request. Please try again later.',
    );
  }
  return body as T;
}

export const login = (email: string, password: string) =>
  request<AuthResponse>('/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

export const register = (name: string, email: string, password: string) =>
  request<RegistrationResponse>('/v1/auth/register', {
    method: 'POST',
    body: JSON.stringify({ name, email, password }),
  });

export const requestPasswordReset = (email: string) =>
  request<{ message: string }>('/v1/auth/password-reset/request', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });

export const confirmPasswordReset = (token: string, newPassword: string) =>
  request<{ message: string }>('/v1/auth/password-reset/confirm', {
    method: 'POST',
    body: JSON.stringify({ token, new_password: newPassword }),
  });

export const refreshAccessToken = (refreshToken: string) =>
  request<AuthResponse>('/v1/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

export const logout = (accessToken: string) =>
  request<{ message: string }>('/v1/auth/logout', {
    method: 'POST',
    body: JSON.stringify({ token: accessToken }),
  });

// fetchSession resolves the caller's identity plus org/project roles and
// feature permissions from an access token — the REST equivalent of the old
// AuthorizeUser gRPC call, used after login/refresh and again after creating
// an organization or project to pick up newly-granted roles.
export const fetchSession = (accessToken: string) =>
  request<SessionResponse>('/v1/auth/session', {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  });

// getOAuthURL returns the provider's Supabase-backed sign-in URL. redirectTo
// must be an absolute URL the browser lands back on after the provider
// redirects to Supabase and Supabase redirects onward — Supabase appends the
// session as a URL fragment there (#access_token=...&refresh_token=...),
// same as the mobile deep-link flow, just with an https URL instead of a
// custom URI scheme.
export const getOAuthURL = (
  provider: 'google' | 'github' | 'linkedin',
  redirectTo: string,
) =>
  request<{ url: string }>(
    `/v1/auth/oauth/${provider}/url?redirect_to=${encodeURIComponent(redirectTo)}`,
  ).then(res => res.url);

// parseOAuthFragment extracts Supabase's implicit-flow tokens from the
// current page's URL fragment (window.location.hash), if present.
export const parseOAuthFragment = (): {
  accessToken: string;
  refreshToken: string;
} | null => {
  const hash = window.location.hash;
  if (!hash || !hash.includes('access_token')) return null;
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const accessToken = params.get('access_token');
  const refreshToken = params.get('refresh_token');
  if (!accessToken || !refreshToken) return null;
  return { accessToken, refreshToken };
};

// parseRecoveryToken extracts the access token Supabase attaches to the
// password-reset email link (#access_token=...&type=recovery). Passed as
// `token` to confirmPasswordReset, which uses it to authorize the password
// change (see telik-auth-service's UpdatePassword).
export const parseRecoveryToken = (): string | null => {
  const hash = window.location.hash;
  if (!hash || !hash.includes('access_token')) return null;
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  return params.get('access_token');
};
